import { HTMLAttributes, ReactNode } from 'react'
import { SanityLink } from '@data/sanity/queries/types/content'
import { PageType } from '@lib/routes'
import {
  ButtonColor,
  ButtonSize,
  ButtonVariant,
} from '@components/buttons/button'
import InternalLink from './internal-link'
import ExternalLink from './external-link'
import CasePageLink from './case-page-link'

type LinkProps = HTMLAttributes<HTMLAnchorElement> & {
  link: SanityLink
  onClick?: () => void
  buttonVariant?: ButtonVariant
  buttonSize?: ButtonSize
  buttonColor?: ButtonColor
  children?: ReactNode
}

const Link = ({
  link,
  tabIndex,
  buttonVariant,
  buttonSize,
  buttonColor,
  'aria-label': ariaLabel,
  onClick,
  children,
  className,
}: LinkProps) => {
  const { id, page, url, casePage, isButton, fontCase, buttonStyles } = link

  // (A) Internal page link
  if (page) {
    return (
      <InternalLink
        pageType={page.type as PageType}
        pageSlug={page.slug}
        id={id}
        tabIndex={tabIndex}
        aria-label={ariaLabel}
        buttonVariant={buttonVariant}
        buttonSize={buttonSize}
        buttonColor={buttonColor}
        buttonStyles={buttonStyles}
        isButton={isButton}
        onClick={onClick}
        fontCase={fontCase}
        className={className}
      >
        {children}
      </InternalLink>
    )
  }

  // (B) External link
  if (url) {
    return (
      <ExternalLink
        url={url}
        id={id}
        tabIndex={tabIndex}
        aria-label={ariaLabel}
        buttonVariant={buttonVariant}
        buttonSize={buttonSize}
        buttonColor={buttonColor}
        buttonStyles={buttonStyles}
        isButton={isButton}
        fontCase={fontCase}
        className={className}
      >
        {children}
      </ExternalLink>
    )
  }

  // (C) Case page link
  if (casePage) {
    return (
      <CasePageLink
        casePage={casePage}
        id={id}
        tabIndex={tabIndex}
        aria-label={ariaLabel}
        buttonVariant={buttonVariant}
        buttonSize={buttonSize}
        buttonColor={buttonColor}
        buttonStyles={buttonStyles}
        isButton={isButton}
        onClick={onClick}
        fontCase={fontCase}
        className={className}
      >
        {children}
      </CasePageLink>
    )
  }

  return null
}

export default Link
